import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from 'react-toastify';
import { fetchCourses, fetchCourseChapters, fetchChapterContent } from '../../api/adminApi';
import { triggerExamAttemptsSync } from '../../api/syncApi';
import { useSocketContext } from '../../context/SocketContext';

const EXAM_SYNC_TYPE = 'exam-single-attempts-sync';

/* Year options for the exam filter: current year down to 2020. */
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [];
for (let y = CURRENT_YEAR; y >= 2020; y -= 1) YEAR_OPTIONS.push(y);

/* Fetch every chapter for a course (paginates past the 100/page admin cap). */
const fetchAllCourseChapters = async (courseId) => {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const data = await fetchCourseChapters(courseId, page, 100);
    all.push(...(data.chapters || []));
    totalPages = data.totalPages || 1;
    page += 1;
  } while (page <= totalPages);
  return all;
};

/* Fetch every content item for a chapter. */
const fetchAllChapterContent = async (chapterId) => {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const data = await fetchChapterContent(chapterId, page, 100);
    all.push(...(data.content || []));
    totalPages = data.totalPages || 1;
    page += 1;
  } while (page <= totalPages);
  return all;
};

const isPracticeExam = (title) =>
  typeof title === 'string' && title.toLowerCase().includes('practice');

/* An exam content item — content_type is "Exam" (per Testpress) and examId is set. */
const isExamContent = (item) =>
  item?.examId != null ||
  (typeof item?.content_type === 'string' && item.content_type.toLowerCase() === 'exam');

/* Syncable exam content: an exam that is NOT a practice exam. */
const isSyncableExamContent = (item) => isExamContent(item) && !isPracticeExam(item?.title);

const yearOf = (dateStr) => {
  if (!dateStr) return null;
  const y = new Date(dateStr).getFullYear();
  return Number.isNaN(y) ? null : y;
};

/* Exam matches the selected year if its created OR modified year equals it. */
const examMatchesYear = (item, year) => {
  if (!year) return true; // "All years"
  const y = Number(year);
  return yearOf(item?.exam?.created) === y || yearOf(item?.exam?.modified) === y;
};

/* ── Single exam row: date range + sync button + live progress ─────────── */
const ExamSyncRow = ({ exam, running, activeExamId, onStarted, index }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [starting, setStarting] = useState(false);

  const practice = isPracticeExam(exam.title);
  // This row owns the running sync if its examId matches — either from the live
  // progress payload (survives reload) or from the locally-tracked trigger.
  const isThisRunning =
    !!running &&
    (Number(running.progress?.examId) === Number(exam.examId) ||
      (running.progress?.examId == null && Number(activeExamId) === Number(exam.examId)));
  const progress = isThisRunning ? running.progress : null;

  const handleSync = async () => {
    if (practice) return;
    setStarting(true);
    try {
      const res = await triggerExamAttemptsSync(exam.examId, {
        date_0: dateFrom || undefined,
        date_1: dateTo || undefined,
      });
      onStarted?.(exam.examId);
      toast.success(res.message || `Sync started for "${exam.title}"`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to start exam attempts sync';
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  // Another exam of this type is syncing — block starting a second one (server 409s anyway).
  const otherRunning = running && !isThisRunning;

  return (
    <tr>
      <td style={{ color: 'var(--text-muted)', width: 32 }}>{index + 1}</td>
      <td>
        {exam.title}
        {practice && (
          <span
            className="data-status-badge"
            style={{
              marginLeft: 8,
              color: 'var(--text-muted)',
              backgroundColor: 'rgba(100,116,139,0.12)',
            }}
          >
            practice
          </span>
        )}
      </td>
      <td>
        <input
          type="date"
          className="schedule-form-input eas-date-input"
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => setDateFrom(e.target.value)}
          disabled={practice || isThisRunning}
          aria-label="From date"
        />
      </td>
      <td>
        <input
          type="date"
          className="schedule-form-input eas-date-input"
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => setDateTo(e.target.value)}
          disabled={practice || isThisRunning}
          aria-label="To date"
        />
      </td>
      <td style={{ minWidth: 160 }}>
        {isThisRunning ? (
          <span className="eas-progress">
            <span className="schedule-spinner" aria-hidden="true" />
            {progress?.total
              ? `${progress.processed || 0}/${progress.total}`
              : 'Starting…'}
          </span>
        ) : (
          <button
            className="qa-btn qa-btn--blue eas-sync-btn"
            onClick={handleSync}
            disabled={practice || starting || otherRunning}
            title={
              practice
                ? 'Practice exams are skipped'
                : otherRunning
                ? 'Another exam sync is already running'
                : 'Sync this exam’s attempts + answers'
            }
          >
            {starting ? (
              <>
                <span className="schedule-spinner" aria-hidden="true" /> Starting…
              </>
            ) : (
              <>
                <i className="bi bi-arrow-repeat" aria-hidden="true" /> Sync
              </>
            )}
          </button>
        )}
      </td>
    </tr>
  );
};

/* Build a parent→children tree from the flat chapter list. */
const buildChapterTree = (chapters) => {
  const byId = new Map();
  chapters.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots = [];
  byId.forEach((node) => {
    const parent = node.parentId != null ? byId.get(node.parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node); // no parent, or parent not in this course's list
  });
  const sortRec = (nodes) => {
    nodes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

/* ── Recursive chapter node (folder tree). Lazy-loads exams on expand. ──── */
const ChapterNode = ({ node, depth, running, activeExamId, onStarted, yearFilter }) => {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState({ loading: false, loaded: false, exams: [] });

  const hasChildren = node.children && node.children.length > 0;

  const handleToggle = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next || state.loaded) return;

    // Load this chapter's own exam contents (children are already in the tree).
    setState({ loading: true, loaded: false, exams: [] });
    try {
      const content = await fetchAllChapterContent(node.id);
      // Non-practice exams only.
      const exams = content.filter(isSyncableExamContent);
      setState({ loading: false, loaded: true, exams });
    } catch (err) {
      console.error(`Error loading content for chapter ${node.id}:`, err);
      setState({ loading: false, loaded: true, exams: [] });
    }
  }, [expanded, state.loaded, node.id]);

  // Apply the year filter to this chapter's own exams.
  const visibleExams = useMemo(
    () => state.exams.filter((e) => examMatchesYear(e, yearFilter)),
    [state.exams, yearFilter]
  );

  return (
    <div className="eas-tree-node">
      <button
        type="button"
        className={`eas-tree-row${expanded ? ' eas-tree-row--open' : ''}`}
        style={{ paddingLeft: 8 + depth * 18 }}
        onClick={handleToggle}
      >
        <i
          className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'} eas-tree-caret`}
          aria-hidden="true"
        />
        <i
          className={`bi ${expanded ? 'bi-folder2-open' : 'bi-folder2'} eas-tree-folder`}
          aria-hidden="true"
        />
        <span className="eas-tree-name">{node.name}</span>
        {hasChildren && (
          <span className="eas-tree-count">{node.children.length}</span>
        )}
      </button>

      {expanded && (
        <div className="eas-tree-children">
          {/* Nested sub-chapters */}
          {node.children.map((child) => (
            <ChapterNode
              key={child.id}
              node={child}
              depth={depth + 1}
              running={running}
              activeExamId={activeExamId}
              onStarted={onStarted}
              yearFilter={yearFilter}
            />
          ))}

          {/* This chapter's own exams */}
          {state.loading ? (
            <div className="data-nested-skeleton" style={{ paddingLeft: 8 + (depth + 1) * 18 }}>
              <Skeleton count={2} height={32} borderRadius={6} />
            </div>
          ) : visibleExams.length > 0 ? (
            <div
              className="data-nested-table-wrap eas-tree-exams"
              style={{ marginLeft: 8 + (depth + 1) * 18 }}
            >
              <table className="data-nested-table eas-exam-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Exam</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleExams.map((exam, idx) => (
                    <ExamSyncRow
                      key={exam.id}
                      exam={exam}
                      running={running}
                      activeExamId={activeExamId}
                      onStarted={onStarted}
                      index={idx}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : state.loaded && !hasChildren ? (
            <div
              className="data-nested-empty"
              style={{ paddingLeft: 8 + (depth + 1) * 18 }}
            >
              {state.exams.length > 0
                ? `No exams match ${yearFilter}.`
                : 'No exams in this chapter.'}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

/* ── Main section ──────────────────────────────────────────────────────── */
const ExamAttemptsSyncSection = () => {
  const { runningSyncs } = useSocketContext();

  const [courseSearch, setCourseSearch] = useState('');
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState(null);

  // Year filter — '' = All years. Filters exams by created/modified year.
  const [yearFilter, setYearFilter] = useState('');

  // examId this client just triggered — bridges the gap before the server's
  // progress payload (which carries examId) arrives.
  const [activeExamId, setActiveExamId] = useState(null);

  // The one running exam-attempts sync (server allows only one at a time).
  const running = useMemo(
    () =>
      (runningSyncs || []).find(
        (s) => s.type === EXAM_SYNC_TYPE && ['running', 'processing'].includes(s.status)
      ) || null,
    [runningSyncs]
  );

  // Clear the local tracker once the sync leaves the running list.
  useEffect(() => {
    if (!running) setActiveExamId(null);
  }, [running]);

  // Load courses (debounced on search).
  useEffect(() => {
    let cancelled = false;
    setCoursesLoading(true);
    const t = setTimeout(() => {
      fetchCourses(1, 50, courseSearch)
        .then((data) => {
          if (!cancelled) setCourses(data.courses || data.results || []);
        })
        .catch(() => toast.error('Failed to load courses'))
        .finally(() => { if (!cancelled) setCoursesLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [courseSearch]);

  const handleSelectCourse = useCallback(async (course) => {
    setSelectedCourse(course);
    setChapters([]);
    setChaptersError(null);
    setChaptersLoading(true);
    try {
      const all = await fetchAllCourseChapters(course.id);
      setChapters(buildChapterTree(all));
    } catch (err) {
      console.error(`Error loading chapters for course ${course.id}:`, err);
      setChaptersError('Failed to load chapters for this course.');
    } finally {
      setChaptersLoading(false);
    }
  }, []);

  return (
    <div className="eas-section">
      <p className="data-page-subtitle" style={{ marginTop: 0 }}>
        Pick a course, drill into its chapters, and sync a non-practice exam&rsquo;s
        attempts &amp; answers fresh from Testpress. Leave the dates blank to sync all
        attempts, or set a range to narrow them.
      </p>

      {/* Course picker */}
      <div className="eas-course-picker theme-card">
        <label className="schedule-form-label" htmlFor="eas-course-search">
          <i className="bi bi-book-fill" aria-hidden="true" /> Select a course
        </label>
        <input
          id="eas-course-search"
          type="text"
          className="schedule-form-input"
          placeholder="Search courses by name…"
          value={courseSearch}
          onChange={(e) => setCourseSearch(e.target.value)}
        />
        <div className="eas-course-list">
          {coursesLoading ? (
            <div className="fast-sync-empty">
              <span className="schedule-spinner" aria-hidden="true" /> Loading courses…
            </div>
          ) : courses.length === 0 ? (
            <div className="fast-sync-empty">No courses found</div>
          ) : (
            courses.map((course) => (
              <button
                key={course.id}
                className={`eas-course-item${selectedCourse?.id === course.id ? ' eas-course-item--selected' : ''}`}
                onClick={() => handleSelectCourse(course)}
              >
                <span className="eas-course-title">{course.title}</span>
                <span className="eas-course-id">ID: {course.id}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chapter tree for selected course */}
      {selectedCourse && (
        <div className="eas-chapters">
          <div className="eas-chapters-toolbar">
            <h3 className="eas-chapters-heading">
              <i className="bi bi-list-nested" aria-hidden="true" />
              {selectedCourse.title}
            </h3>
            <div className="eas-year-filter">
              <label htmlFor="eas-year-select">
                <i className="bi bi-calendar3" aria-hidden="true" /> Year
              </label>
              <select
                id="eas-year-select"
                className="schedule-form-input eas-year-select"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <option value="">All years</option>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {chaptersError && (
            <div className="data-error-banner">
              <i className="bi bi-exclamation-triangle-fill" /> {chaptersError}
            </div>
          )}

          {chaptersLoading ? (
            <Skeleton count={5} height={52} borderRadius={8} style={{ marginBottom: 8 }} />
          ) : chapters.length === 0 && !chaptersError ? (
            <div className="data-nested-empty">No chapters found for this course.</div>
          ) : (
            <div className="eas-tree theme-card">
              {chapters.map((node) => (
                <ChapterNode
                  key={node.id}
                  node={node}
                  depth={0}
                  running={running}
                  activeExamId={activeExamId}
                  onStarted={setActiveExamId}
                  yearFilter={yearFilter}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExamAttemptsSyncSection;
